import React, { useRef, useState } from "react";
import { fileUrl } from "@/lib/api";

const fmt = (n) => Number(n).toFixed(1);

const toLocal = (el, e, rot) => {
  const r = el.getBoundingClientRect();
  const dx = e.clientX - (r.left + r.width / 2), dy = e.clientY - (r.top + r.height / 2);
  const a = (-rot * Math.PI) / 180;
  const x = dx * Math.cos(a) - dy * Math.sin(a) + el.offsetWidth / 2;
  const y = dx * Math.sin(a) + dy * Math.cos(a) + el.offsetHeight / 2;
  return { x: Math.min(1, Math.max(0, x / el.offsetWidth)), y: Math.min(1, Math.max(0, y / el.offsetHeight)) };
};

function Stamp({ a, marks }) {
  const pos = { left: `${a.x * 100}%`, top: `${a.y * 100}%` };
  if (a.type === "mark") {
    const v = marks[a.q];
    return <div style={pos} className="absolute -translate-y-1/2 pointer-events-none" data-testid={`stamp-mark-q${a.q + 1}`}>
      <div className="border-2 border-emerald-500 bg-emerald-50/90 rounded-lg px-3 py-1 shadow text-center">
        <p className="font-mono font-black text-emerald-700 text-lg leading-tight">{v == null ? "?" : `+${fmt(v)}`}</p>
        <p className="text-[9px] font-bold text-emerald-800">Q{a.q + 1} · Assigned by Teacher</p>
      </div>
    </div>;
  }
  if (a.type === "correct" || a.type === "wrong") {
    const ok = a.type === "correct";
    return <div style={pos} className={`absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none font-black text-lg tracking-wide ${ok ? "text-emerald-600" : "text-red-600"}`}>{ok ? "✓ CORRECT" : "✗ WRONG"}</div>;
  }
  if (a.type === "comment") {
    return <div style={pos} className="absolute -translate-y-1/2 pointer-events-none max-w-[45%] bg-amber-100 border border-amber-400 text-amber-900 text-xs font-semibold rounded px-2 py-1 shadow">{a.text}</div>;
  }
  const box = { left: `${a.x * 100}%`, top: `${a.y * 100}%`, width: `${a.w * 100}%`, height: `${a.h * 100}%` };
  if (a.type === "highlight") return <div style={box} className="absolute pointer-events-none bg-yellow-300/40 mix-blend-multiply" />;
  return <div style={box} className="absolute pointer-events-none border-[3px] border-red-500 rounded-[50%]" />;
}

export function CopyCanvas({ src, annotations, marks, rot = 0, zoom = 1, tool, onAdd, readOnly }) {
  const ref = useRef(null);
  const [drag, setDrag] = useState(null);
  const isBox = tool === "highlight" || tool === "circle";
  const down = (e) => { if (readOnly || !isBox) return; setDrag({ s: toLocal(ref.current, e, rot), c: toLocal(ref.current, e, rot) }); };
  const move = (e) => { if (drag) setDrag({ ...drag, c: toLocal(ref.current, e, rot) }); };
  const up = () => {
    if (!drag) return;
    const { s, c } = drag; setDrag(null);
    const w = Math.abs(c.x - s.x), h = Math.abs(c.y - s.y);
    if (w > 0.01 && h > 0.005) onAdd({ type: tool, x: Math.min(s.x, c.x), y: Math.min(s.y, c.y), w, h });
  };
  const click = (e) => { if (!readOnly && tool && !isBox) onAdd({ type: tool, ...toLocal(ref.current, e, rot) }); };
  const live = drag && { type: tool, x: Math.min(drag.s.x, drag.c.x), y: Math.min(drag.s.y, drag.c.y), w: Math.abs(drag.c.x - drag.s.x), h: Math.abs(drag.c.y - drag.s.y) };
  return (
    <div className="p-6 flex justify-center min-w-fit">
      <div ref={ref} onClick={click} onMouseDown={down} onMouseMove={move} onMouseUp={up} data-testid="copy-canvas"
        style={{ width: `${zoom * 820}px`, transform: `rotate(${rot}deg)` }}
        className={`relative bg-white shadow-xl select-none transition-transform ${!readOnly && tool ? "cursor-crosshair" : ""}`}>
        <img src={fileUrl(src.replace("/api", ""))} alt="answer copy page" draggable={false} className="w-full block" data-testid="copy-page-img" />
        {annotations.map((a, i) => <Stamp key={i} a={a} marks={marks} />)}
        {live && <Stamp a={live} marks={marks} />}
      </div>
    </div>
  );
}

export function PageTabs({ pages, page, setPage }) {
  return <div className="flex gap-1.5 overflow-x-auto">
    {pages.map((_, i) => (
      <button key={i} onClick={() => setPage(i)} data-testid={`copy-page-tab-${i}`} className={`px-3 py-1 rounded text-xs font-bold whitespace-nowrap ${page === i ? "bg-blue-600 text-white" : "bg-white border border-slate-300 text-slate-600"}`}>Page {i + 1}{page === i ? " (Active)" : ""}</button>
    ))}
  </div>;
}
